import useMessage from '@/hooks/useMessage'
import usePersistDataStore from '@/stores/persistData'
import { LOGIN_TYPE } from '@/utils/const'
import { Button, Checkbox, Form, Input, Select, SelectProps, Space, Tabs, TabsProps } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { areaCodeLists } from '@/utils/const'
import { doLoginV2, getVerifyCodeByPhone } from '@/api/login'
import appConfig from '@/config'

type LabelRender = SelectProps['labelRender']

const LoginForm: React.FC<{ onClickForgot: () => void }> = ({ onClickForgot }) => {
  const { t } = useTranslation()
  const { message } = useMessage()
  const navigate = useNavigate()
  const lang = usePersistDataStore(state => state.lang)
  const rememberEmailOptions = usePersistDataStore(state => state.rememberEmailOptions)
  const rememberPhoneOptions = usePersistDataStore(state => state.rememberPhoneOptions)
  const setRememberEmailOptions = usePersistDataStore(state => state.setRememberEmailOptions)
  const setRememberPhoneOptions = usePersistDataStore(state => state.setRememberPhoneOptions)
  const logIn = usePersistDataStore(state => state.logIn)
  const [form] = Form.useForm()
  const [rememberPhone, setRememberPhone] = useState(false)
  const [rememberEmail, setRememberEmail] = useState(false)
  const [type, setType] = useState(LOGIN_TYPE.EMAIL)
  const [initialValues, setInitialValues] = useState({
    phone: '',
    email: '',
    phonePassword: '',
    emailPassword: '',
    verifyCode: '',
    areaCode: '+61',
  })

  const [codeTimer, setCodeTimer] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (codeTimer === 60) {
      intervalRef.current = setInterval(() => {
        setCodeTimer(pre => pre - 1)
      }, 1000)
    } else if (codeTimer === 0) {
      clearInterval(intervalRef.current as NodeJS.Timeout)
    }
  }, [codeTimer])

  const [areaCodeOptions, setAreaCodeOptions] = useState<{ label: string; value: string; key: string | number }[]>([])
  useEffect(() => {
    setAreaCodeOptions(
      areaCodeLists.map((item, index) => ({
        label: (lang === 'en_US' ? item.en : item.cn) + ' ' + item.value,
        value: item.value,
        key: index,
      }))
    )
  }, [lang])
  const labelRender: LabelRender = props => {
    return <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{props.value}</span>
  }

  const verify_pwd = useRef('')

  const items: TabsProps['items'] = [
    {
      key: LOGIN_TYPE.PHONE.toString(),
      label: t('user.mobileLogin'),
    },
    {
      key: LOGIN_TYPE.EMAIL.toString(),
      label: t('user.emailLogin'),
    },
  ]

  useEffect(() => {
    form.resetFields()
  }, [type])

  const doGetCode = async () => {
    const phone = form.getFieldValue('phone')
    const phone_code = form.getFieldValue('areaCode')
    if (phone && phone !== '') {
      let res: any
      try {
        res = await getVerifyCodeByPhone({ phone_code, phone, type: 'log' })
      } catch (error: any) {
        console.log(error)
        message.error(error.message || error.msg || error)
        return
      }
      if (res.code === 1001) {
        message.success(t('user.sendCodeSuccess'))
        verify_pwd.current = res.data.code
        setCodeTimer(60)
      } else {
        message.error(res.msg)
        return
      }
    } else {
      message.error(t('user.phoneEmpty'))
    }
  }

  const doLogin = async () => {
    let values
    try {
      values = await form.validateFields()
    } catch (error) {
      console.log(error)
      return
    }
    // console.log(values)
    const params: { [key: string]: string | number | undefined } = {
      type,
    }
    if (type === LOGIN_TYPE.PHONE) {
      params.account = values.phone
      params.password = values.phonePassword
      params.areaCode = values.areaCode
    } else if (type === LOGIN_TYPE.EMAIL) {
      params.account = values.email
      params.password = values.emailPassword
    } else if (type === LOGIN_TYPE.VERIFY_CODE) {
      params.account = values.phone
      params.verify_code = values.verifyCode
      params.verify_pwd = verify_pwd.current
      params.areaCode = values.areaCode
    }
    let res: any
    try {
      res = await doLoginV2(params)
    } catch (error: any) {
      console.log(error)
      message.error(error.message || error.msg || error)
      return
    }
    if (res.code === 1001) {
      if (res.data.prefix !== appConfig.prefix) {
        message.error('login.wrongCompany')
        return
      } else {
        if (type === LOGIN_TYPE.PHONE) {
          let password = ''
          if (rememberPhone) {
            password = btoa(values.phonePassword)
          }
          setRememberPhoneOptions({
            phone: values.phone,
            phonePassword: password,
            areaCode: values.areaCode,
            remember: rememberPhone,
          })
        } else if (type === LOGIN_TYPE.EMAIL) {
          let password = ''
          if (rememberEmail) {
            password = btoa(values.emailPassword)
          }
          setRememberEmailOptions({
            email: values.email,
            emailPassword: password,
            remember: rememberEmail,
          })
        }
        logIn(res.data)
        navigate('/')
      }
    } else {
      console.log(res)
      message.error(res.msg)
      return
    }
  }

  useEffect(() => {
    setInitialValues({
      phone: rememberPhoneOptions.phone,
      email: rememberEmailOptions.email,
      areaCode: rememberPhoneOptions.areaCode,
      phonePassword: atob(rememberPhoneOptions.phonePassword),
      emailPassword: atob(rememberEmailOptions.emailPassword),
      verifyCode: '',
    })
    setRememberEmail(rememberEmailOptions.remember)
    setRememberPhone(rememberPhoneOptions.remember)
  }, [])

  const validateEmailFormat = (_: any, value: string) => {
    if (type === LOGIN_TYPE.EMAIL) {
      const reg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9._%+-]+(\.[a-zA-Z0-9_-]+)+$/
      if (!reg.test(value)) {
        return Promise.reject(t('user.emailFormatError'))
      } else {
        return Promise.resolve()
      }
    } else {
      return Promise.resolve()
    }
  }

  const validateEmailNotEmpty = (_: any, value: string) => {
    if (type === LOGIN_TYPE.EMAIL && (value === undefined || value === '')) {
      return Promise.reject(t('user.cantEmpty'))
    } else {
      return Promise.resolve()
    }
  }

  const validatePhoneNotEmpty = (_: any, value: string) => {
    if (type === LOGIN_TYPE.PHONE && (value === undefined || value === '')) {
      return Promise.reject(t('user.cantEmpty'))
    } else {
      return Promise.resolve()
    }
  }

  const validateVerifyCodeNotEmpty = (_: any, value: string) => {
    if (type === LOGIN_TYPE.VERIFY_CODE && (value === undefined || value === '')) {
      return Promise.reject(t('user.cantEmpty'))
    } else {
      return Promise.resolve()
    }
  }

  return (
    <>
      <div className='login-form-title'>{t('user.login')}</div>
      <Tabs
        className='login-form-tabs'
        direction='rtl'
        defaultActiveKey='2'
        items={items}
        indicator={{ size: origin => origin - 30, align: 'center' }}
        onChange={key => setType(Number(key))}
      />
      <div className='login-form'>
        <Form form={form} layout='vertical' initialValues={initialValues}>
          {/* email */}
          <Form.Item
            name='email'
            label={t('user.email')}
            hidden={type !== LOGIN_TYPE.EMAIL}
            rules={[{ validator: validateEmailNotEmpty }, { validator: validateEmailFormat }]}>
            <Input placeholder={t('user.enterEmail')} />
          </Form.Item>
          <Form.Item
            name='emailPassword'
            label={t('user.password')}
            hidden={type !== LOGIN_TYPE.EMAIL}
            rules={[{ validator: validateEmailNotEmpty }]}>
            <Input.Password placeholder={t('user.enterPassword')} />
          </Form.Item>

          {/* phone */}
          <Form.Item label={t('user.phoneNumber')} hidden={type === LOGIN_TYPE.EMAIL}>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name='areaCode' noStyle>
                <Select
                  style={{ width: '100px' }}
                  options={areaCodeOptions}
                  labelRender={labelRender}
                  popupMatchSelectWidth={200}
                />
              </Form.Item>
              <Form.Item
                noStyle
                name='phone'
                rules={[{ validator: validatePhoneNotEmpty }, { validator: validateVerifyCodeNotEmpty }]}>
                <Input style={{ width: 'calc(100% - 100px)' }} placeholder={t('user.enterPhone')} />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item label={t('user.password')} hidden={type !== LOGIN_TYPE.PHONE}>
            <div className='extra-btn' onClick={() => setType(LOGIN_TYPE.VERIFY_CODE)}>
              {t('user.verificationCode')}
            </div>
            <Form.Item noStyle name='phonePassword' rules={[{ validator: validatePhoneNotEmpty }]}>
              <Input.Password placeholder={t('user.enterPassword')} />
            </Form.Item>
          </Form.Item>
          <Form.Item label={t('user.verificationCode')} hidden={type !== LOGIN_TYPE.VERIFY_CODE}>
            <div className='extra-btn' onClick={() => setType(LOGIN_TYPE.PHONE)}>
              {t('user.password')}
            </div>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name='verifyCode' noStyle rules={[{ validator: validateVerifyCodeNotEmpty }]}>
                <Input style={{ width: 'calc(100% - 90px)' }} placeholder={t('user.enterVerificationCode')} />
              </Form.Item>
              <Button onClick={doGetCode} disabled={codeTimer !== 0} style={{ width: '90px' }}>
                {codeTimer === 0 ? t('user.getCode') : codeTimer}
              </Button>
            </Space.Compact>
          </Form.Item>
        </Form>
        <div className='form-footer'>
          <Checkbox
            checked={type === LOGIN_TYPE.EMAIL ? rememberEmail : rememberPhone}
            onChange={e =>
              type === LOGIN_TYPE.EMAIL ? setRememberEmail(e.target.checked) : setRememberPhone(e.target.checked)
            }>
            <span className='remember-text'>{t('user.remember')}</span>
          </Checkbox>
          <div className='forget-password' onClick={onClickForgot}>
            {t('user.forgotPassword')}
          </div>
        </div>
        <div className='form-btn'>
          <Button style={{ width: '100%' }} type='primary' onClick={doLogin}>
            {t('user.login')}
          </Button>
        </div>
        <div className='register-tip'>
          <span>{t('user.registerTip')}</span>
          <span className='to-register' onClick={() => navigate('/register')}>
            {t('user.toRegister')}
          </span>
        </div>
      </div>
    </>
  )
}

export default LoginForm
